import { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { ref, push, get, update } from 'firebase/database';
import { Spinner } from '../ui/Spinner';
import { useBranches } from '../../hooks/useBranches';
import { resolveBranchRef } from '../../utils/branchResolver';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';

const RecordPaymentModal = ({ isOpen, onClose, onSuccess, paymentToEdit }) => {
  const { byId: branchesById, bySlug: branchesBySlug } = useBranches();
  const { methods: receptionMethods } = usePaymentMethods('reception');
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  const [formData, setFormData] = useState({
    studentId: '',
    courseId: '',
    batchId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    receiptId: '',
    paymentType: 'registration',
    notes: '',
    status: 'completed',
  });

  // Fetch students and courses on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const studentsSnapshot = await get(ref(db, 'students'));
        const coursesSnapshot = await get(ref(db, 'courses'));

        const studentsData = studentsSnapshot.val() || {};
        setStudents(
          Object.entries(studentsData).map(([id, data]) => ({
            id,
            userId: data.userId || id,
            name: `${data.personalDetails?.firstName || ''} ${
              data.personalDetails?.lastName || ''
            }`.trim(),
            ...data,
          })),
        );

        // Get courses with their batches
        const coursesData = coursesSnapshot.val() || {};
        setCourses(
          Object.entries(coursesData).map(([courseId, courseData]) => ({
            id: courseId,
            name: courseData.name,
            ...courseData,
          })),
        );

        // Extract batches from courses
        const allBatches = [];
        Object.entries(coursesData).forEach(([courseId, courseData]) => {
          if (courseData.batches) {
            Object.entries(courseData.batches).forEach(
              ([batchKey, batchData]) => {
                if (typeof batchData === 'object' && batchData !== null) {
                  allBatches.push({
                    id: batchKey,
                    courseId: courseId,
                    batchId: batchData.batchId || '',
                    branch: batchData.branch || '',
                    name: `${batchData.batchId || ''} - ${
                      batchData.branch || ''
                    }`,
                    ...batchData,
                  });
                }
              },
            );
          }
        });
        setBatches(allBatches);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  // Update form data when editing a payment
  useEffect(() => {
    if (paymentToEdit) {
      setFormData({
        studentId: paymentToEdit.studentId || '',
        courseId: paymentToEdit.courseId || '',
        batchId: paymentToEdit.batchId || '',
        amount: paymentToEdit.amount || '',
        paymentDate:
          paymentToEdit.paymentDate || new Date().toISOString().split('T')[0],
        paymentMethod: paymentToEdit.paymentMethod || 'cash',
        receiptId: paymentToEdit.receiptId || '',
        paymentType: paymentToEdit.paymentType || 'registration',
        notes: paymentToEdit.notes || '',
        status: paymentToEdit.status || 'completed',
      });
    } else {
      // Reset form for new payment
      setFormData({
        studentId: '',
        courseId: '',
        batchId: '',
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'cash',
        receiptId: '',
        paymentType: 'registration',
        notes: '',
        status: 'completed',
      });
    }
  }, [paymentToEdit]);

  // Update handleSubmit to handle both new and edit cases
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const selectedStudent = students.find((s) => s.id === formData.studentId);
      const selectedCourse = courses.find((c) => c.id === formData.courseId);
      const selectedBatch = batches.find((b) => b.id === formData.batchId);

      const paymentData = {
        ...formData,
        amount: parseFloat(formData.amount),
        studentName: selectedStudent?.name || '',
        studentUserId: selectedStudent?.userId || '',
        courseName: selectedCourse?.name || '',
        batchName: selectedBatch?.name || '',
        updatedAt: new Date().toISOString(),
      };

      const paymentRef = ref(
        db,
        paymentToEdit ? `payments/${paymentToEdit.id}` : 'payments',
      );

      if (paymentToEdit) {
        // Update existing payment
        await update(paymentRef, paymentData);
      } else {
        // Create new payment
        paymentData.createdAt = new Date().toISOString();
        await push(paymentRef, paymentData);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBatches = batches.filter(
    (batch) => batch.courseId === formData.courseId,
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
      <div className="w-full md:w-[800px] m-4">
        <div className="bg-white rounded-lg shadow-xl max-h-[calc(100vh-2rem)] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg md:text-xl font-medium text-gray-900">
              Record Payment
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 md:p-6">
            <div className="md:grid md:grid-cols-2 md:gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Student Information Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 bg-gray-50 p-2 rounded">
                    Student Information
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Student
                      </label>
                      <select
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.studentId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            studentId: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Student</option>
                        {students.map((student) => (
                          <option key={student.id} value={student.id}>
                            {student.name || student.id}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Course
                      </label>
                      <select
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.courseId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            courseId: e.target.value,
                            batchId: '',
                          })
                        }
                        required
                      >
                        <option value="">Select Course</option>
                        {courses.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Batch
                      </label>
                      <select
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.batchId}
                        onChange={(e) =>
                          setFormData({ ...formData, batchId: e.target.value })
                        }
                        required
                      >
                        <option value="">Select Batch</option>
                        {filteredBatches.map((batch) => {
                          const resolved = resolveBranchRef(batch, {
                            byId: branchesById,
                            bySlug: branchesBySlug,
                          });
                          return (
                            <option key={batch.id} value={batch.id}>
                              {batch.batchId}
                              {resolved?.name ? ` - ${resolved.name}` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 bg-gray-50 p-2 rounded">
                    Additional Information
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      rows="5"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="mt-6 md:mt-0 space-y-6">
                {/* Payment Details Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 bg-gray-50 p-2 rounded">
                    Payment Details
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount
                      </label>
                      <input
                        type="number"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.amount}
                        onChange={(e) =>
                          setFormData({ ...formData, amount: e.target.value })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Type
                      </label>
                      <select
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.paymentType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentType: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="registration">Registration</option>
                        <option value="installment">Installment</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method
                      </label>
                      <select
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.paymentMethod}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentMethod: e.target.value,
                          })
                        }
                        required
                      >
                        {receptionMethods.length === 0 ? (
                          <>
                            <option value="cash">Cash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="card">Card</option>
                            <option value="cheque">Cheque</option>
                          </>
                        ) : (
                          receptionMethods.map((method) => (
                            <option
                              key={method.id}
                              value={method.type || method.id}
                            >
                              {method.label}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Receipt ID
                      </label>
                      <input
                        type="text"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.receiptId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            receiptId: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Date
                      </label>
                      <input
                        type="date"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.paymentDate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentDate: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="mt-6 pt-4 border-t sticky bottom-0 bg-white">
              <div className="flex flex-col-reverse sm:flex-row justify-end space-y-reverse space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  {loading ? <Spinner size="sm" /> : 'Save Payment'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RecordPaymentModal;
